/**
 * Risk profiler display helpers for the admin investor profile.
 * Question/option labels mirror greenback-capital-client (read-only).
 * Do not map schemas onto each other; detect current (6 answers + totalScore),
 * previous 15-question (totalScore + 15q ids), and legacy (averageScore).
 */

import { formatSnakeCaseToLabel } from './helpers'

export type RiskProfileBand = 'low' | 'moderate_low' | 'moderate' | 'moderate_high' | 'high'

export type LegacyRiskProfileBand =
  | 'conservative'
  | 'moderate'
  | 'balanced'
  | 'growth'
  | 'aggressive'

export type RiskProfileSchema = 'current' | 'v15' | 'legacy' | 'unknown'

export type RiskQuestionOption = {
  id: string
  label: string
}

export type RiskQuestion = {
  id: string
  question: string
  options: RiskQuestionOption[]
}

export type RiskAnswerValue = string | string[]
export type RiskProfilerAnswers = Record<string, RiskAnswerValue>

export type RiskProfileAnswersPayload = {
  answers?: RiskProfilerAnswers
  /** Current 6-answer schema (5 scored, 25–100) or previous 15-question schema (15–75). */
  totalScore?: number
  /** Legacy 6-question schema. */
  averageScore?: number
  riskBand?: string | null
} | null

/** Product labels shown to investors for current bands. */
const NEW_RISK_BAND_LABELS: Record<RiskProfileBand, string> = {
  low: 'Conservative',
  moderate_low: 'Moderate',
  moderate: 'Balanced',
  moderate_high: 'Growth',
  high: 'Aggressive',
}

/** Current investor-app questionnaire (6 answers: 5 scored 5/10/15/20 + unscored txn value). */
export const RISK_PROFILER_QUESTIONS: RiskQuestion[] = [
  {
    id: 'investment_horizon',
    question: 'What is your intended investment horizon?',
    options: [
      { id: 'horizon_under_1', label: 'Less than 1 year' },
      { id: 'horizon_1_3', label: '1–3 years' },
      { id: 'horizon_3_5', label: '3–5 years' },
      { id: 'horizon_5_10', label: '5–10 years' },
    ],
  },
  {
    id: 'risk_tolerance',
    question:
      'What best describes your risk tolerance for your investing activities through Credibila?',
    options: [
      { id: 'risk_conservative', label: 'Conservative' },
      { id: 'risk_moderate', label: 'Moderate' },
      { id: 'risk_aggressive', label: 'Aggressive' },
      { id: 'risk_significant', label: 'Significant Risk' },
    ],
  },
  {
    id: 'investing_experience_duration',
    question: 'How long have you been investing?',
    options: [
      { id: 'exp_none', label: 'No experience' },
      { id: 'exp_1_3', label: '1–3 years' },
      { id: 'exp_3_5', label: '3–5 years' },
      { id: 'exp_over_5', label: 'More than 5 years' },
    ],
  },
  {
    id: 'investing_knowledge',
    question: 'What best describes your level of investing knowledge?',
    options: [
      { id: 'knowledge_none', label: 'No knowledge' },
      { id: 'knowledge_limited', label: 'Limited' },
      { id: 'knowledge_good', label: 'Good' },
      { id: 'knowledge_sophisticated', label: 'Sophisticated' },
    ],
  },
  {
    id: 'previous_investment_types',
    question: 'What types of investments have you previously held?',
    options: [
      { id: 'held_cash', label: 'Cash/deposits' },
      { id: 'held_bonds_funds', label: 'Bonds or mutual funds' },
      { id: 'held_equities_etfs', label: 'Public equities or ETFs' },
      {
        id: 'held_pe_vc_complex',
        label: 'Private equity, venture capital or complex products',
      },
    ],
  },
  {
    id: 'expected_transactional_value',
    question: 'What is your expected transaction value through Credibila?',
    options: [
      { id: 'txn_up_to_1000', label: 'Up to USD 1,000' },
      { id: 'txn_above_1000', label: 'Above USD 1,000' },
    ],
  },
]

const CURRENT_QUESTION_IDS = new Set(RISK_PROFILER_QUESTIONS.map((q) => q.id))

/** Stored 15-question schema — display only; new completions use RISK_PROFILER_QUESTIONS. */
export const LEGACY_15_RISK_PROFILER_QUESTIONS: RiskQuestion[] = [
  {
    id: 'primary_investment_objective',
    question: 'What is your primary investment objective?',
    options: [
      { id: 'capital_preservation', label: 'Capital preservation' },
      { id: 'income_generation', label: 'Income generation' },
      { id: 'balanced_income_growth', label: 'Balanced income and growth' },
      { id: 'long_term_capital_growth', label: 'Long-term capital growth' },
      { id: 'aggressive_capital_growth', label: 'Aggressive capital growth' },
    ],
  },
  {
    id: 'investment_horizon',
    question: 'What is your intended investment horizon?',
    options: [
      { id: 'horizon_under_1', label: 'Less than 1 year' },
      { id: 'horizon_1_3', label: '1–3 years' },
      { id: 'horizon_3_5', label: '3–5 years' },
      { id: 'horizon_5_10', label: '5–10 years' },
      { id: 'horizon_over_10', label: 'More than 10 years' },
    ],
  },
  {
    id: 'attitude_toward_risk',
    question: 'Which statement best describes your attitude toward risk?',
    options: [
      { id: 'protect_capital', label: 'I prioritise protecting my capital' },
      { id: 'limited_risk', label: 'I accept limited risk' },
      {
        id: 'moderate_risk',
        label: 'I accept moderate risk for potential growth',
      },
      {
        id: 'significant_volatility',
        label: 'I am comfortable with significant volatility',
      },
      {
        id: 'substantial_risk',
        label: 'I am willing to accept substantial risk for higher potential returns',
      },
    ],
  },
  {
    id: 'loss_impact',
    question: 'If you lost a significant portion of this investment, what would the impact be?',
    options: [
      { id: 'impact_severe', label: 'Severe financial impact' },
      { id: 'impact_significant', label: 'Significant impact' },
      { id: 'impact_moderate', label: 'Moderate impact' },
      { id: 'impact_limited', label: 'Limited impact' },
      { id: 'impact_minimal', label: 'Minimal impact' },
    ],
  },
  {
    id: 'discretionary_capital_share',
    question: 'What percentage of your discretionary capital does this investment represent?',
    options: [
      { id: 'capital_over_75', label: 'More than 75%' },
      { id: 'capital_50_75', label: '50–75%' },
      { id: 'capital_25_50', label: '25–50%' },
      { id: 'capital_10_25', label: '10–25%' },
      { id: 'capital_under_10', label: 'Less than 10%' },
    ],
  },
  {
    id: 'illiquid_five_years_obligations',
    question:
      'If this investment became completely illiquid for five years, could you meet your financial obligations without it?',
    options: [
      { id: 'obligations_no', label: 'No' },
      { id: 'obligations_probably_not', label: 'Probably not' },
      { id: 'obligations_possibly', label: 'Possibly' },
      { id: 'obligations_yes', label: 'Yes' },
      { id: 'obligations_yes_comfortably', label: 'Yes, comfortably' },
    ],
  },
  {
    id: 'no_guaranteed_exit_comfort',
    question: 'How long are you comfortable keeping an investment without a guaranteed exit?',
    options: [
      { id: 'exit_under_1', label: 'Less than 1 year' },
      { id: 'exit_1_3', label: '1–3 years' },
      { id: 'exit_3_5', label: '3–5 years' },
      { id: 'exit_5_10', label: '5–10 years' },
      { id: 'exit_over_10', label: 'More than 10 years' },
    ],
  },
  {
    id: 'investing_experience_duration',
    question: 'How long have you been investing?',
    options: [
      { id: 'exp_none', label: 'No experience' },
      { id: 'exp_under_1', label: 'Less than 1 year' },
      { id: 'exp_1_3', label: '1–3 years' },
      { id: 'exp_3_5', label: '3–5 years' },
      { id: 'exp_over_5', label: 'More than 5 years' },
    ],
  },
  {
    id: 'previous_investment_types',
    question: 'What types of investments have you previously held?',
    options: [
      { id: 'held_cash', label: 'Cash/deposits only' },
      { id: 'held_bonds_funds', label: 'Bonds or mutual funds' },
      { id: 'held_equities_etfs', label: 'Public equities or ETFs' },
      { id: 'held_alternatives', label: 'Alternative investments' },
      {
        id: 'held_pe_vc_complex',
        label: 'Private equity, venture capital or complex products',
      },
    ],
  },
  {
    id: 'understanding_of_investment_risk',
    question: 'How would you describe your understanding of investment risk?',
    options: [
      { id: 'understand_very_limited', label: 'Very limited' },
      { id: 'understand_basic', label: 'Basic' },
      { id: 'understand_moderate', label: 'Moderate' },
      { id: 'understand_good', label: 'Good' },
      { id: 'understand_advanced', label: 'Advanced' },
    ],
  },
  {
    id: 'investment_decision_frequency',
    question: 'How frequently do you make investment decisions?',
    options: [
      { id: 'freq_never', label: 'Never' },
      { id: 'freq_rarely', label: 'Rarely' },
      { id: 'freq_occasionally', label: 'Occasionally' },
      { id: 'freq_regularly', label: 'Regularly' },
      { id: 'freq_frequently', label: 'Frequently' },
    ],
  },
  {
    id: 'understand_alts_illiquid',
    question:
      'Do you understand that alternative investments may be illiquid and may not provide a regular opportunity to sell?',
    options: [
      { id: 'alts_illiquid_no', label: 'No' },
      { id: 'alts_illiquid_partially', label: 'Partially' },
      { id: 'alts_illiquid_yes', label: 'Yes' },
    ],
  },
  {
    id: 'understand_private_no_market_value',
    question:
      'Do you understand that private investments may not have a readily available or frequently updated market value?',
    options: [
      { id: 'private_nav_no', label: 'No' },
      { id: 'private_nav_partially', label: 'Partially' },
      { id: 'private_nav_yes', label: 'Yes' },
    ],
  },
  {
    id: 'understand_capital_loss',
    question: 'Do you understand that you may lose some or all of the capital invested?',
    options: [
      { id: 'capital_loss_no', label: 'No' },
      { id: 'capital_loss_partially', label: 'Partially' },
      { id: 'capital_loss_yes', label: 'Yes' },
    ],
  },
  {
    id: 'understand_past_performance',
    question: 'Do you understand that past performance does not guarantee future results?',
    options: [
      { id: 'past_perf_no', label: 'No' },
      { id: 'past_perf_partially', label: 'Partially' },
      { id: 'past_perf_yes', label: 'Yes' },
    ],
  },
]

const V15_ONLY_QUESTION_IDS = new Set(
  LEGACY_15_RISK_PROFILER_QUESTIONS.map((q) => q.id).filter((id) => !CURRENT_QUESTION_IDS.has(id)),
)

function findRiskQuestion(questionId: string): RiskQuestion | undefined {
  return (
    RISK_PROFILER_QUESTIONS.find((q) => q.id === questionId) ??
    LEGACY_15_RISK_PROFILER_QUESTIONS.find((q) => q.id === questionId)
  )
}

export function isV15RiskProfileSchema(answers: RiskProfileAnswersPayload | undefined): boolean {
  if (typeof answers?.totalScore !== 'number') return false
  const keys = answers.answers ? Object.keys(answers.answers) : []
  return keys.some((key) => V15_ONLY_QUESTION_IDS.has(key))
}

/** Current 6-answer schema (totalScore, no 15-question-only ids). */
export function isNewRiskProfileSchema(answers: RiskProfileAnswersPayload | undefined): boolean {
  return typeof answers?.totalScore === 'number' && !isV15RiskProfileSchema(answers)
}

export function isLegacyRiskProfileSchema(answers: RiskProfileAnswersPayload | undefined): boolean {
  return typeof answers?.averageScore === 'number' && typeof answers?.totalScore !== 'number'
}

export function getRiskProfileSchema(
  answers: RiskProfileAnswersPayload | undefined,
): RiskProfileSchema {
  if (isNewRiskProfileSchema(answers)) return 'current'
  if (isV15RiskProfileSchema(answers)) return 'v15'
  if (isLegacyRiskProfileSchema(answers)) return 'legacy'
  return 'unknown'
}

export function getAnswerOptionIds(value: RiskAnswerValue | undefined): string[] {
  if (Array.isArray(value)) return value.filter((id) => typeof id === 'string' && id.length > 0)
  if (typeof value === 'string' && value.length > 0) return [value]
  return []
}

export function getRiskProfileBandLabel(band: string | null | undefined): string {
  if (!band) return '—'
  if (band in NEW_RISK_BAND_LABELS) {
    return NEW_RISK_BAND_LABELS[band as RiskProfileBand]
  }
  return formatSnakeCaseToLabel(band)
}

export function getRiskAnswerLabel(questionId: string, optionId: RiskAnswerValue): string {
  const optionIds = getAnswerOptionIds(optionId)
  if (optionIds.length === 0) return '—'

  const question = findRiskQuestion(questionId)
  const orderedIds = question
    ? [
        ...question.options.map((option) => option.id).filter((id) => optionIds.includes(id)),
        ...optionIds.filter((id) => !question.options.some((option) => option.id === id)),
      ]
    : optionIds

  return orderedIds
    .map((id) => {
      const option = question?.options.find((o) => o.id === id)
      return option?.label ?? formatSnakeCaseToLabel(id)
    })
    .join(', ')
}

export function getRiskQuestionLabel(questionId: string): string {
  const question = findRiskQuestion(questionId)
  return question?.question ?? formatSnakeCaseToLabel(questionId)
}

export type RiskAnswerRow = {
  questionId: string
  questionLabel: string
  optionId: string
  optionLabel: string
}

function rowsFromQuestionList(
  questions: RiskQuestion[],
  answers: RiskProfilerAnswers,
): RiskAnswerRow[] {
  const listedIds = new Set(questions.map((q) => q.id))
  const ordered = questions.flatMap((question) => {
    const optionIds = getAnswerOptionIds(answers[question.id])
    if (optionIds.length === 0) return []
    return [
      {
        questionId: question.id,
        questionLabel: question.question,
        optionId: optionIds.join(','),
        optionLabel: getRiskAnswerLabel(question.id, optionIds),
      },
    ]
  })
  const extra = Object.entries(answers)
    .filter(([questionId]) => !listedIds.has(questionId))
    .flatMap(([questionId, optionValue]) => {
      const optionIds = getAnswerOptionIds(optionValue)
      if (optionIds.length === 0) return []
      return [
        {
          questionId,
          questionLabel: getRiskQuestionLabel(questionId),
          optionId: optionIds.join(','),
          optionLabel: getRiskAnswerLabel(questionId, optionIds),
        },
      ]
    })
  return [...ordered, ...extra]
}

export function getRiskAnswerRows(
  answers: RiskProfilerAnswers | undefined,
  schema: RiskProfileSchema,
): RiskAnswerRow[] {
  if (!answers || Object.keys(answers).length === 0) return []

  if (schema === 'current') {
    return rowsFromQuestionList(RISK_PROFILER_QUESTIONS, answers)
  }

  if (schema === 'v15') {
    return rowsFromQuestionList(LEGACY_15_RISK_PROFILER_QUESTIONS, answers)
  }

  return Object.entries(answers).flatMap(([questionId, optionValue]) => {
    const optionIds = getAnswerOptionIds(optionValue)
    if (optionIds.length === 0) return []
    return [
      {
        questionId,
        questionLabel: getRiskQuestionLabel(questionId),
        optionId: optionIds.join(','),
        optionLabel: getRiskAnswerLabel(questionId, optionIds),
      },
    ]
  })
}

export function hasRiskProfileAnswers(answers: RiskProfileAnswersPayload | undefined): boolean {
  if (!answers || typeof answers !== 'object') return false
  const answerKeys = answers.answers ? Object.keys(answers.answers) : []
  return (
    answerKeys.length > 0 ||
    typeof answers.totalScore === 'number' ||
    typeof answers.averageScore === 'number' ||
    Boolean(answers.riskBand)
  )
}
